import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarCheck, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, cx } from "./ui";
import { fromLocalInput, timeOnly, toLocalInput } from "../lib/datetime";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDate, formatDateTime } from "../lib/format";
import type { Booking, BookingWrite } from "../api/types";

/**
 * Reserve a slot: pick a person, a service, a provider, then a time.
 *
 * The slot list comes from `/bookings/availability`, and it is the answer the
 * server has already decided: every chip shown is free under the account's
 * lead-time and window rules (FR-10.3) and under every booking in the way
 * (FR-10.2). The client never second-guesses the grid -- that is the whole of
 * the "the server decides" contract this panel exists to honour.
 *
 * The confirmation posts ``starts_at`` as the instant the slot returned; if the
 * slot was taken in the milliseconds since the grid loaded, the server's 409
 * names it and the grid refreshes.
 */
export function BookingPanel({
  onClose,
  onCreated,
  preselect,
}: {
  onClose: () => void;
  onCreated?: ((booking: Booking) => void) | undefined;
  preselect?: {
    contactId?: string;
    serviceId?: string;
    employeeId?: string;
    date?: string;
  };
}) {
  const timeZone = useAccountTimeZone();
  const today = () => toLocalInput(new Date().toISOString(), timeZone).slice(0, 10);

  const [contactId, setContactId] = useState<string>(preselect?.contactId ?? "");
  const [serviceId, setServiceId] = useState<string>(preselect?.serviceId ?? "");
  const [employeeId, setEmployeeId] = useState<string>(preselect?.employeeId ?? "");
  const [date, setDate] = useState<string>(preselect?.date ?? today());
  const [chosen, setChosen] = useState<string>("");
  const [error, setError] = useState<string>("");
  // A second click can arrive before React commits `book.isPending`; the ref
  // closes that window so one reservation cannot become two POSTs.
  const inFlight = useRef(false);

  const contacts = useQuery({
    queryKey: ["booking-panel-contacts"],
    queryFn: ({ signal }) => api.contacts({ page_size: 300 }, signal),
    staleTime: 10 * 60_000,
  });
  const products = useQuery({
    queryKey: ["product-options"],
    queryFn: ({ signal }) => api.productOptions(signal),
    staleTime: 10 * 60_000,
  });
  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 10 * 60_000,
  });

  const startOfDay = () => fromLocalInput(`${date}T00:00`, timeZone);
  const endOfDay = () => fromLocalInput(`${date}T23:59`, timeZone);

  const slotsQuery = useQuery({
    queryKey: ["availability", serviceId, employeeId, date],
    queryFn: ({ signal }) =>
      api.bookingAvailability(
        {
          service: serviceId || undefined,
          employee: employeeId || undefined,
          from: startOfDay() ?? undefined,
          to: endOfDay() ?? undefined,
        },
        signal,
      ),
    enabled: Boolean(serviceId && employeeId && date),
  });
  const slots = slotsQuery.data ?? [];

  const duration = products.data?.find((p) => p.id === serviceId)?.effective_duration_minutes;

  const book = useMutation({
    mutationFn: (body: BookingWrite) => api.createBooking(body),
    onSuccess: (booking) => {
      onCreated?.(booking);
      onClose();
    },
  });

  const submitError = book.error instanceof ApiError ? book.error : null;
  const submit = () => {
    if (!chosen) {
      setError("Pick a slot to book.");
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setError("");
    book.mutate(
      {
        contact: contactId || null,
        service: serviceId || null,
        employee: employeeId || null,
        starts_at: chosen,
        quantity: 1,
        source: "staff",
        notes: "",
      },
      {
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  };

  const person = contacts.data?.results.find((c) => c.id === contactId);

  return (
    <div className="w-[26rem] max-w-full rounded-lg bg-white p-3 text-left ring-1 ring-inset ring-slate-300">
      <p className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-900">
        Reserve a slot
        <button
          onClick={onClose}
          aria-label="Close"
          className="rounded p-0.5 text-slate-500 hover:bg-slate-100"
        >
          <X size={13} />
        </button>
      </p>

      <div className="grid gap-2">
        <label className="block text-xs font-medium text-slate-700">
          Person
          <select
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
            aria-label="Who the slot is for"
            className="mt-0.5 w-full rounded-md border-0 bg-white px-2 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          >
            <option value="">Blocked slot — nobody yet</option>
            {(contacts.data?.results ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs font-medium text-slate-700">
            Service
            <select
              value={serviceId}
              onChange={(event) => { setServiceId(event.target.value); setChosen(""); }}
              aria-label="Service"
              className="mt-0.5 w-full rounded-md border-0 bg-white px-2 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            >
              <option value="">Choose…</option>
              {(products.data ?? []).filter((p) => p.is_bookable).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.effective_duration_minutes ?? "?"}m
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-slate-700">
            Provider
            <select
              value={employeeId}
              onChange={(event) => { setEmployeeId(event.target.value); setChosen(""); }}
              aria-label="Provider"
              className="mt-0.5 w-full rounded-md border-0 bg-white px-2 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            >
              <option value="">Choose…</option>
              {(employees.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-xs font-medium text-slate-700">
          Day
          <input
            type="date"
            value={date}
            min={today()}
            onChange={(event) => { setDate(event.target.value); setChosen(""); }}
            aria-label="Day to book"
            className="mt-0.5 w-full rounded-md border-0 bg-white px-2 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </label>
      </div>

      {serviceId && employeeId ? (
        slotsQuery.isPending ? (
          <p className="mt-2 text-xs text-slate-400">Finding free slots…</p>
        ) : slots.length === 0 ? (
          <p className="mt-2 text-xs text-slate-500">
            No free slots on {formatDate(fromLocalInput(`${date}T00:00`, timeZone), timeZone)}.
            {duration ? `The service needs ${duration} minutes.` : ""}
          </p>
        ) : (
          <div className="mt-2 max-h-40 overflow-auto rounded-md bg-slate-50 p-2">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Free slots · {formatDate(slots[0]!.starts_at, timeZone)}
            </p>
            <div className="flex flex-wrap gap-1">
              {slots.map((slot) => (
                <button
                  key={slot.starts_at}
                  onClick={() => setChosen(slot.starts_at)}
                  aria-pressed={chosen === slot.starts_at}
                  className={cx(
                    "rounded-md px-2 py-1 text-xs font-medium tabular-nums",
                    chosen === slot.starts_at
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-100",
                  )}
                >
                  {timeOnly(slot.starts_at, timeZone)}
                </button>
              ))}
            </div>
          </div>
        )
      ) : (
        <p className="mt-2 text-[11px] text-slate-400">
          Pick a service and a provider to see what is free.
        </p>
      )}

      {chosen && person ? (
        <p className="mt-1 text-[11px] text-slate-500">
          {person.display_name} · {formatDateTime(chosen, timeZone)}
        </p>
      ) : null}

      {submitError ? (
        <p role="alert" className="mt-1 text-xs text-rose-700">
          {submitError.code === "booking_slot_conflict"
            ? "That slot was just taken. Pick another from the grid."
            : submitError.message}
        </p>
      ) : error ? (
        <p role="alert" className="mt-1 text-xs text-rose-700">
          {error}
        </p>
      ) : null}

      <div className="mt-3 flex items-center gap-1.5">
        <Button
          variant="primary"
          disabled={book.isPending || !chosen}
          onClick={submit}
        >
          <CalendarCheck size={13} />
          {book.isPending ? "Booking…" : "Book this slot"}
        </Button>
        <Button onClick={onClose} disabled={book.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}